/*
Persistent cache of the last confirmed RoundAggregate.

The webapp normally re-fetches the full confirmed aggregate from the server on
every load (`getAggregate`). This module persists that aggregate in IndexedDB
so a previously-loaded round can open while the device is offline (primarily
the iPad Capacitor app). Unconfirmed actions stored in `unconfirmedActions2`
are replayed on top of the cached aggregate exactly as they are on top of a
freshly-downloaded one.

Correctness/invalidations:
  - A cached row is only served when its schemaHash, userProfileHash,
    viewerTrack, and viewerUserId all match the current session, and the stored
    aggregate still parses against the current Zod schema. Anything else is
    treated as a cache miss and the row is deleted. viewerUserId matters
    because aggregate anonymization is per-viewer, not just per-track: a
    restricted funder's hidden recommenders' notes are deleted based on WHO is
    viewing, while two same-track viewers share a userProfileHash — without the
    viewer in the key, an admin switching impersonation between them could be
    served the other viewer's note visibility from the cache.
  - All writes are best-effort: a failed cache write (quota, transient
    IndexedDB disconnect) must never break the action pipeline. Failures are
    reported to Sentry and otherwise swallowed.
*/

import {
  roundAggregateSchema,
  type RoundAggregate,
} from "@engine/round/aggregate";
import { type Track } from "@lib/types";
import { recentDurabilityFailures } from "@lib/offline/durabilityFailureLog";
import { captureMessage } from "@lib/telemetry/sentry";
import { type EntityTable } from "dexie";

export type RoundAggregateCacheRecord = {
  aggregateId: string;
  aggregate: RoundAggregate;
  version: number;
  cachedAt: number;
  schemaHash: string;
  userProfileHash: string;
  viewerTrack: Track;
  // The effective viewer the aggregate was anonymized for. Optional for
  // back-compat with rows written before this field existed (those mismatch on
  // read and are discarded — a one-time re-download). Non-indexed, so no Dexie
  // version bump needed.
  viewerUserId?: string | null;
};

export type RoundAggregateCacheTable = EntityTable<
  RoundAggregateCacheRecord,
  "aggregateId"
>;

export type RoundAggregateCacheKey = {
  aggregateId: string;
  schemaHash: string;
  userProfileHash: string;
  viewerTrack: Track;
  viewerUserId: string | null;
};

// Permanently stop persisting aggregates. Called on sign-out before clearing,
// so a non-blocking aggregate write can't recreate the cleared SPAStorage DB
// before the page redirects (which would leave the previous user's round cached
// for the next offline session).
let writesSuspended = false;
export function suspendAggregateCacheWrites(): void {
  writesSuspended = true;
}

// Tracks every in-flight write so sign-out can await them before deleting the
// DB. `writesSuspended` only stops writes that haven't yet passed the guard
// below; a write already inside its IndexedDB transaction would otherwise
// complete after the delete and recreate the DB with the previous user's round.
// A Set (entries removed on settle) rather than a promise chain, so a long
// offline session that persists after every action doesn't retain every prior
// settled write.
const inFlightWrites = new Set<Promise<unknown>>();
export function flushAggregateCacheWrites(): Promise<void> {
  return Promise.allSettled(inFlightWrites).then(() => {});
}

export type WriteCachedAggregateOptions = {
  /**
   * True when `aggregate` is what the server just handed this load — a fresh
   * `getAggregate` download, not a socket-advanced snapshot. Lets the guard
   * below DELETE a stored row that sits ahead of it; see the comment there.
   */
  fromFreshDownload?: boolean;
};

/** Best-effort: failures are logged to Sentry and swallowed. */
export async function writeCachedAggregate(
  table: RoundAggregateCacheTable,
  key: RoundAggregateCacheKey,
  aggregate: RoundAggregate,
  options: WriteCachedAggregateOptions = {},
): Promise<void> {
  if (writesSuspended) return;
  const record: RoundAggregateCacheRecord = {
    aggregateId: key.aggregateId,
    aggregate,
    version: aggregate.version,
    cachedAt: Date.now(),
    schemaHash: key.schemaHash,
    userProfileHash: key.userProfileHash,
    viewerTrack: key.viewerTrack,
    viewerUserId: key.viewerUserId,
  };
  const work = (async () => {
    const startedAt = performance.now();
    let stage = "starting_transaction";
    try {
      // Read-then-write in one rw transaction so the version check is atomic: a
      // lagging writer (e.g. a second tab sharing this IndexedDB) must not
      // overwrite a newer cached snapshot with an older one. On the iPad shell the
      // cache is single-WebView so this can't actually race, but the guard keeps
      // the cache correct under any consumer and costs ~one indexed read.
      await table.db.transaction("rw", table, () => {
        stage = "reading_existing";
        return table
          .get(key.aggregateId)
          .then((existing) => {
            // Keep this as a Dexie promise chain instead of an `async` callback.
            // Dexie can commit a native IndexedDB transaction before a transpiled
            // async callback settles, which surfaces as PrematureCommitError.
            // Skip if the stored row is from a strictly newer aggregate version,
            // OR — at the SAME version — from a more recently *initiated* write
            // (`cachedAt`). Writes are non-blocking, so an in-flight write from an
            // older adapter (e.g. before an impersonation / viewer-track switch)
            // could land after a newer adapter wrote its row; the version is
            // unchanged but its key (userProfileHash/viewerTrack) is stale. Without
            // the cachedAt tiebreak it would clobber the current session's row, and
            // readCachedAggregate would then miss → the round shows unavailable
            // offline despite having just been loaded.
            //
            // One exception, and it DELETES rather than overwrites: a row ahead of
            // an aggregate the server just handed us describes history the server
            // doesn't have. That happens when the action log is replaced underneath
            // a browser — staging's nightly reset from production, a DB restore, a
            // round import. Kept, such a row survives forever (no later write can
            // reach a version the server never produces), costing the round its
            // cache-first fast path and, on the iPad, serving pre-reset content
            // offline. Deleting is deliberately blunt: we make no attempt to tell
            // that apart from a second tab that confirmed an action mid-load, and
            // do NOT write our own aggregate over it — dropping the row can only
            // cost a re-download, whereas overwriting it would let an older
            // aggregate be served as if it were current.
            if (existing != null && existing.version > aggregate.version) {
              if (!options.fromFreshDownload) return;
              stage = "deleting_ahead_row";
              return table.delete(key.aggregateId).then(() => undefined);
            }
            if (
              existing != null &&
              existing.version === aggregate.version &&
              existing.cachedAt > record.cachedAt
            ) {
              return;
            }
            stage = "writing_record";
            return table.put(record);
          })
          .then(() => {
            stage = "committing_transaction";
          });
      });
    } catch (error) {
      // Snapshot at failure, before the asynchronous storage probe. Only
      // diagnostic metadata is retained; never attach the aggregate's content.
      const extra = {
        aggregateId: key.aggregateId,
        aggregateVersion: aggregate.version,
        fromFreshDownload: options.fromFreshDownload ?? false,
        transactionStage: stage,
        transactionElapsedMs: Math.round(performance.now() - startedAt),
        errors: describeCacheErrors(error),
        recentDurabilityFailures: recentDurabilityFailures(),
      };
      const message = `Failed to persist round aggregate cache for ${key.aggregateId}: ${String(error)}`;
      // Detached: a slow/hung storage probe must not hold up the next cache
      // write or sign-out's flush. Still emit the warning if the API is absent,
      // throws, rejects, or never settles. Keep the message for issue grouping.
      void (async () => {
        let timeout: ReturnType<typeof setTimeout> | undefined;
        let storageEstimate: StorageEstimate | null = null;
        try {
          if (typeof navigator !== "undefined" && navigator.storage?.estimate) {
            storageEstimate = await Promise.race([
              navigator.storage.estimate(),
              new Promise<null>((resolve) => {
                timeout = setTimeout(() => resolve(null), 3_000);
              }),
            ]);
          }
        } catch {
          // Storage diagnostics are best-effort, just like the cache itself.
        } finally {
          clearTimeout(timeout);
        }
        captureMessage(message, {
          level: "warning",
          extra: {
            ...extra,
            storageUsageBytes: storageEstimate?.usage ?? null,
            storageQuotaBytes: storageEstimate?.quota ?? null,
          },
        });
      })().catch(() => {
        // A telemetry failure must never become an unhandled rejection.
      });
    }
  })();
  // Track this write so sign-out can await it before deleting the DB; remove it
  // on settle so the set doesn't grow across a long offline session.
  inFlightWrites.add(work);
  void work.finally(() => inFlightWrites.delete(work));
  await work;
}

type CacheErrorDetails = {
  path: string;
  message: string;
  name?: string;
  stack?: string;
};

/** Flatten nested wrappers so Sentry's normalization depth won't hide the cause. */
function describeCacheErrors(
  error: unknown,
  path = "error",
  depth = 0,
): CacheErrorDetails[] {
  if (error == null) return [];
  const details = error as {
    name?: unknown;
    stack?: unknown;
    inner?: unknown;
    cause?: unknown;
  };
  const entry = {
    path,
    message: String(error),
    name: typeof details.name === "string" ? details.name : undefined,
    stack: typeof details.stack === "string" ? details.stack : undefined,
  };
  // Bound traversal even when an error chain has cycles.
  if (depth === 3) return [entry];
  return [
    entry,
    ...describeCacheErrors(details.inner, `${path}.inner`, depth + 1),
    ...describeCacheErrors(details.cause, `${path}.cause`, depth + 1),
  ];
}

/**
 * Why a cache read produced no usable record. Reported (via the round-open
 * RUM signal) so misses in the field are attributable — a deploy that changed
 * the schema, a fresh device, a corrupt row — without needing a repro.
 */
export type CacheReadMissReason =
  | "no_row"
  | "read_error"
  | "schema_changed"
  | "profile_changed"
  | "track_changed"
  | "viewer_changed"
  | "invalid_row";

/**
 * Returns the cached record when it is valid for the given session key, or
 * the reason it wasn't (deleting the stale/corrupt row) otherwise.
 */
export async function readCachedAggregateDetailed(
  table: RoundAggregateCacheTable,
  key: RoundAggregateCacheKey,
): Promise<
  | { record: RoundAggregateCacheRecord; missReason: null }
  | { record: null; missReason: CacheReadMissReason }
> {
  let record: RoundAggregateCacheRecord | undefined;
  try {
    record = await table.get(key.aggregateId);
  } catch (error) {
    captureMessage(
      `Failed to read round aggregate cache for ${key.aggregateId}: ${String(
        error,
      )}`,
      { level: "warning" },
    );
    return { record: null, missReason: "read_error" };
  }
  if (record == null) {
    return { record: null, missReason: "no_row" };
  }

  if (
    record.schemaHash !== key.schemaHash ||
    record.userProfileHash !== key.userProfileHash ||
    record.viewerTrack !== key.viewerTrack ||
    // Pre-viewerUserId rows have `undefined` here and so always mismatch —
    // deliberate: they can't prove which viewer they were anonymized for.
    record.viewerUserId !== key.viewerUserId
  ) {
    // First mismatch in check order names the miss; schema first, since a
    // deploy-driven schema change is the fleet-wide case worth spotting.
    const missReason: CacheReadMissReason =
      record.schemaHash !== key.schemaHash
        ? "schema_changed"
        : record.userProfileHash !== key.userProfileHash
          ? "profile_changed"
          : record.viewerTrack !== key.viewerTrack
            ? "track_changed"
            : "viewer_changed";
    await deleteCachedAggregate(table, key.aggregateId);
    return { record: null, missReason };
  }

  const parsed = roundAggregateSchema.safeParse(record.aggregate);
  if (!parsed.success) {
    captureMessage(
      `Cached round aggregate for ${key.aggregateId} failed schema validation; discarding`,
      { level: "warning" },
    );
    await deleteCachedAggregate(table, key.aggregateId);
    return { record: null, missReason: "invalid_row" };
  }

  return { record: { ...record, aggregate: parsed.data }, missReason: null };
}

/** readCachedAggregateDetailed for callers that don't report the miss. */
export async function readCachedAggregate(
  table: RoundAggregateCacheTable,
  key: RoundAggregateCacheKey,
): Promise<RoundAggregateCacheRecord | null> {
  return (await readCachedAggregateDetailed(table, key)).record;
}

/** Best-effort: failures are swallowed (the row will be overwritten on the next online load). */
export async function deleteCachedAggregate(
  table: RoundAggregateCacheTable,
  aggregateId: string,
): Promise<void> {
  try {
    await table.delete(aggregateId);
  } catch {
    // Best-effort; a stale row is harmless because reads re-validate.
  }
}
